<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import waveBottom3 from '../../assets/stepper/wave-bottom-3.svg?url'
import waveBottom4 from '../../assets/stepper/wave-bottom-4.svg?url'
import waveBottom5 from '../../assets/stepper/wave-bottom-5.svg?url'
import waveTop3 from '../../assets/stepper/wave-top-3.svg?url'
import waveTop4 from '../../assets/stepper/wave-top-4.svg?url'
import waveTop5 from '../../assets/stepper/wave-top-5.svg?url'

const { items, dashStyle } = defineProps<{
  title: StoryBlokHeading
  subtitle?: string
  dashStyle: 'wave' | 'line' | 'none'
  button?: StoryBlokComponent[]
  items: {
    _editable: string
    title: string
    text: StoryBlokRichText
    icon?: StoryBlokIcon
    image?: StoryBlokImage
  }[]
  textAlignment?: string
  checkMark: string
}>()

const waveSrc: any = {
  waveBottom3,
  waveBottom4,
  waveBottom5,
  waveTop3,
  waveTop4,
  waveTop5,
}

const breakpoints = useBreakpoints(breakpointsTailwind)
const isWave = dashStyle === 'wave' && breakpoints.greater('xl')
const lastIndex = items.length - 1
const gap = items.length === 3 ? '90px' : items.length === 4 ? '40px' : '20px'

const renderComponent = ref(true)
async function forceRender() {
  renderComponent.value = false
  await nextTick()
  renderComponent.value = true
}

onBeforeMount(() => forceRender())
</script>

<template>
  <div>
    <div class="mx-auto">
      <StoryblokHeading class="sm:text-center" :title="title" />
      <div v-if="subtitle" class="text-large mt-10px sm:text-center">
        {{ subtitle }}
      </div>
      <div v-if="renderComponent" class="mt-6 flex flex-col md:flex-row" :style="{ gap: isWave ? gap : '20px' }">
        <div v-for="(item, index) in items" v-editable="item" class="flex flex-1 gap-10px md:flex-col">
          <div class="flex flex-col gap-10px items-center md:flex-row" :class="{ 'justify-center': isWave || dashStyle === 'none' }">
            <StoryblokImage v-if="item.image?.filename" :src="item.image" />
            <AppIcon v-else-if="item.icon?.icon" v-bind="item.icon" size="50" msize="30" class="text-primary" />
            <div v-else class="stepper-circle">
              <span class="text-primary font-semibold lg:text-24px">{{ index + 1 }}</span>
            </div>
            <hr v-if="!isWave && dashStyle !== 'none' && index !== lastIndex" class="stepper-dashed-line">
            <div v-else-if="index !== lastIndex && dashStyle !== 'none'" :class="{ 'w-full': !isWave, 'self-start': index % 2 !== 0 && isWave }">
              <img class="absolute" :src="waveSrc[`wave${index % 2 === 0 ? 'Bottom' : 'Top'}${items.length}`]" alt="wave">
            </div>
          </div>
          <div class="flex flex-col <md:ml-3">
            <h4 :class="`md:${textAlignment}`">
              {{ item.title }}
            </h4>
            <div v-if="item.text" class="text-large mt-10px">
              <StoryblokRichText :document="item.text" :class="[{ 'checklist ': checkMark === 'true' }, `md:${textAlignment}`]" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="button?.length" class="md:flex md:justify-center md:mt-10 md:gap-2">
        <StoryblokButton v-for="item in button" v-bind="item" class="<md:w-full <md:mt-2" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stepper-circle {
  @apply min-w-30px min-h-30px flex items-center justify-center border-2 rounded-full border-primary
  lg:min-w-50px lg:min-h-50px;
}
.stepper-dashed-line {
  @apply border-r-2 h-full border-primary border-opacity-40 border-dashed
  md:border-t-2 md:w-full md:h-2px;
}
.checklist {
  @apply text-lg mt-2.5;
  :deep(li) {
    @apply list-none flex
    before:content-[''] before:border-b-2 before:border-r-2 before:border-success before:transform before:rotate-45 before:h-4 before:mr-4 before:min-w-2 before:relative before:top-1 before:basis-8px;
    & + li {
      @apply mt-2.5;
    }
  }
  :deep(ul) {
      @apply pl-4px;
    }
}
</style>
